import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/common/Header";
import StyledIcon from "@/components/common/StyledIcon";
import FooterSection from "@/components/homepage/FooterSection";


const Index = () => {
  const navigate = useNavigate();

  try {
    return (
      <div className="flex min-h-screen flex-col bg-[#F7F5F2] font-nunito">
        <Header showActions={false} />
        <main className="flex-1">
          <section className="bg-gradient-to-b from-[#F7F5F2] to-white py-20 md:py-24">
            <div className="section-container">
              <div className="flex flex-col items-center text-center">
                <div className="mb-6 inline-flex items-center rounded-full bg-[#E8E1EA] px-[14px] py-[6px] text-[14px] font-medium text-[#6C4F77]">
                  Made for thoughtful people 💜
                </div>
                <h1 className="mb-8 text-4xl font-bold tracking-tight text-[#6C4F77] md:text-5xl lg:text-6xl">
                  Never forget what matters most — {" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6C4F77] to-[#F4978E]">
                    even the little things
                  </span>
                </h1>
                <p className="mb-10 max-w-2xl text-[#2C2C2C] text-lg md:text-xl">
                  Wabisabi helps you remember the small details that deepen your connections — from favorite foods to life milestones. Thoughtful nudges, meaningful memories, and stronger relationships in one place.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    size="lg"
                    className="bg-[#6C4F77] text-white hover:bg-[#5A3A68] font-semibold"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Get Started — It's Free
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="text-[#6C4F77] border-[#6C4F77] hover:bg-[#F7F5F2] font-semibold"
                    onClick={() => navigate("/auth")}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="section-padding bg-[#F7F5F2]">
            <div className="section-container">
              <div className="section-title text-center">
                <h2 className="mb-6 text-3xl font-bold text-[#6C4F77] md:text-4xl">How It Works</h2>
                <p className="mx-auto max-w-2xl text-[#2C2C2C]">
                  A simple way to remember what's important about the people you care about
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-3">
                <div className="flex flex-col items-center text-center shadow-soft-hover rounded-xl p-8 hover:translate-y-[-5px] transition-all duration-300 bg-white/60 backdrop-blur-sm">
                  <StyledIcon 
                    icon="📝" 
                    variant="coral" 
                    size="md"
                    className="mb-6" 
                  />
                  <h3 className="mb-4 text-xl font-semibold text-[#6C4F77]">Log Meaningful Details</h3>
                  <p className="text-[#2C2C2C]">
                    <span className="italic text-[#2C2C2C]/80 block mb-3">Keep track of what makes them feel seen.</span>
                    Like "Mom loves lilies" or "Arjun's love language is acts of service."
                  </p>
                </div>

                <div className="flex flex-col items-center text-center shadow-soft-hover rounded-xl p-8 hover:translate-y-[-5px] transition-all duration-300 bg-white/60 backdrop-blur-sm">
                  <StyledIcon 
                    icon="⏰" 
                    variant="yellow" 
                    size="md"
                    className="mb-6" 
                  />
                  <h3 className="mb-4 text-xl font-semibold text-[#6C4F77]">Get Timely Reminders</h3>
                  <p className="text-[#2C2C2C]">
                    <span className="italic text-[#2C2C2C]/80 block mb-3">Customizable reminders right when they matter.</span>
                    Never miss a birthday, anniversary, or moment to reconnect.
                  </p>
                </div>

                <div className="flex flex-col items-center text-center shadow-soft-hover rounded-xl p-8 hover:translate-y-[-5px] transition-all duration-300 bg-white/60 backdrop-blur-sm">
                  <StyledIcon 
                    icon="💡" 
                    variant="plum" 
                    size="md"
                    className="mb-6" 
                  />
                  <h3 className="mb-4 text-xl font-semibold text-[#6C4F77]">Receive Smart Suggestions</h3>
                  <p className="text-[#2C2C2C]">
                    <span className="italic text-[#2C2C2C]/80 block mb-3">Let your notes become thoughtful actions.</span>
                    Like "Sophie mentioned sushi — reserve her favorite spot."
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="section-padding bg-[#F7F5F2]">
            <div className="section-container">
              <div className="section-title text-center">
                <h2 className="mb-6 text-3xl font-bold text-[#6C4F77] md:text-4xl">What People Are Saying</h2>
                <p className="mx-auto max-w-2xl text-[#2C2C2C]">
                  Join thousands who are strengthening their relationships with Wabisabi
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-xl bg-white/60 backdrop-blur-sm p-8 shadow-soft-hover hover:shadow-lg transition-all duration-300">
                  <p className="mb-6 text-[#2C2C2C]">
                    "I used to forget important details about my friends and family. With Wabisabi, I'm now the person who always remembers the little things."
                  </p>
                  <div className="font-medium text-[#6C4F77]">Sarah T.</div>
                </div>
                
                <div className="rounded-xl bg-white/60 backdrop-blur-sm p-8 shadow-soft-hover hover:shadow-lg transition-all duration-300">
                  <p className="mb-6 text-[#2C2C2C]">
                    "The gift suggestions are spot on! I got my dad exactly what he wanted for his birthday, based on preferences I'd logged months ago."
                  </p>
                  <div className="font-medium text-[#6C4F77]">Michael R.</div>
                </div>
                
                <div className="rounded-xl bg-white/60 backdrop-blur-sm p-8 shadow-soft-hover hover:shadow-lg transition-all duration-300">
                  <p className="mb-6 text-[#2C2C2C]">
                    "As someone with ADHD, this app has been a game-changer for maintaining my social connections and remembering important details."
                  </p>
                  <div className="font-medium text-[#6C4F77]">Jamie L.</div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-24 md:py-32 bg-[#6C4F77] text-white">
            <div className="section-container">
              <div className="flex flex-col items-center justify-center rounded-xl p-10 text-center max-w-3xl mx-auto">
                <div className="mb-6 flex items-center justify-center p-3 rounded-xl bg-[#FCEBED] border-[1.5px] border-[#F4978E] transition-all duration-300 hover:bg-[#F4978E] hover:text-white" style={{boxShadow: '0 10px 25px rgba(0, 0, 0, 0.06)'}}>
                  <span className="text-[20px]">✨</span>
                </div>
                <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">You don't need to remember everything — just what matters.</h2>
                <p className="mb-8 max-w-xl text-[#F7F5F2] opacity-90">
                  We'll help with the rest.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-[#F4978E] text-white hover:bg-[#E3867A] font-semibold"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Try Wabisabi Free
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <FooterSection />
      </div>
    );
  } catch (error) {
    console.error("Error rendering Index component:", error);
    return <div>Error loading page</div>;
  }
};

export default Index;
